import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { useState } from "react";
import { useStore } from "../../context/StoreContext";
import { fmtTRY } from "../../lib/format";

export default function CartPage() {
  const { cart, updateItem, removeItem, applyCoupon, removeCoupon } = useStore();
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const lines = cart?.lines || [];
  const threshold = cart?.free_shipping_threshold || 0;
  const missing = cart?.amount_to_free_shipping || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet><title>Sepetim | BIGELDİ</title></Helmet>
      <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mb-8" data-testid="cart-page-title">
        Sepetim <span className="text-neutral-400 text-lg font-medium">({cart?.item_count || 0} ürün)</span>
      </h1>

      {lines.length === 0 ? (
        <div className="py-24 text-center">
          <p className="text-neutral-400 mb-6">Sepetiniz boş</p>
          <Link to="/ara" className="inline-block bg-[#121214] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-amber-600 transition-colors" data-testid="cart-empty-shop-btn">Alışverişe Başla</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {cart?.issues?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 space-y-1" data-testid="cart-page-issues">
                {cart.issues.map((i, idx) => <p key={idx}>⚠ {i.issue}</p>)}
              </div>
            )}
            {lines.map((line) => (
              <div key={`${line.product_id}-${line.variant_id || "base"}`} className="flex gap-4 bg-white rounded-2xl border border-[#E6E4DD] p-4" data-testid="cart-page-item">
                <Link to={`/urun/${line.slug}`} className="w-24 h-28 rounded-xl overflow-hidden bg-[#F4F2EB] shrink-0">
                  {line.image && <img src={line.image} alt={line.name} className="w-full h-full object-cover" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3">
                    <div>
                      <Link to={`/urun/${line.slug}`} className="font-semibold text-sm hover:text-amber-700">{line.name}</Link>
                      {line.variant_label && <p className="text-xs text-neutral-500 mt-0.5">{line.variant_label}</p>}
                      {line.customization && (
                        <p className="text-xs text-amber-700 mt-1 flex items-center gap-2" data-testid="cart-customization">
                          {line.customization.photo_url && <img src={line.customization.photo_url} alt="Kişiselleştirme" className="w-8 h-8 rounded object-cover border border-[#E6E4DD]" />}
                          <span>Kişiselleştirme: {line.customization.text ? `"${line.customization.text}"` : "fotoğraf eklendi"}</span>
                        </p>
                      )}
                      <p className="font-mono-ed text-[10px] text-neutral-400 mt-0.5">{line.sku}</p>
                      {line.campaign_name && <p className="text-[11px] text-amber-700 mt-1">{line.campaign_name}</p>}
                    </div>
                    <button onClick={() => removeItem(line.product_id, line.variant_id)} className="text-neutral-300 hover:text-rose-600 h-fit" data-testid="cart-page-remove" aria-label="Kaldır"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-[#E6E4DD] rounded-full">
                      <button className="px-3 py-2 disabled:opacity-30" disabled={line.quantity <= 1} onClick={() => updateItem(line.product_id, line.variant_id, line.quantity - 1)} data-testid="cart-page-minus"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm font-bold" data-testid="cart-page-qty">{line.quantity}</span>
                      <button className="px-3 py-2 disabled:opacity-30" disabled={line.quantity >= line.stock} onClick={() => updateItem(line.product_id, line.variant_id, line.quantity + 1)} data-testid="cart-page-plus"><Plus size={14} /></button>
                    </div>
                    <div className="text-right">
                      {line.final_unit_price < line.unit_price && <p className="text-xs text-neutral-400 line-through">{fmtTRY(line.unit_price * line.quantity)}</p>}
                      <p className="font-mono-ed font-bold">{fmtTRY(line.line_total)}</p>
                      <p className="text-[10px] text-neutral-400">Stok: {line.stock}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="bg-white rounded-2xl border border-[#E6E4DD] p-6 h-fit sticky top-36" data-testid="cart-summary">
            {threshold > 0 && (
              <div className="mb-5">
                {missing > 0
                  ? <p className="text-xs text-neutral-600 mb-2 flex items-center gap-1.5"><Truck size={14} className="text-amber-600" /><b>{fmtTRY(missing)}</b>&nbsp;daha ekleyin, kargo bedava!</p>
                  : <p className="text-xs text-emerald-700 font-semibold mb-2 flex items-center gap-1.5"><Truck size={14} /> Ücretsiz kargo kazandınız</p>}
                <div className="h-1.5 bg-[#F4F2EB] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all" style={{ width: `${Math.min(100, ((threshold - missing) / threshold) * 100)}%` }} />
                </div>
              </div>
            )}
            <div className="mb-5">
              {cart?.coupon_code ? (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <span className="font-mono-ed text-sm font-semibold text-emerald-800">{cart.coupon_code}</span>
                  <button onClick={removeCoupon} className="text-xs text-rose-600 font-semibold" data-testid="coupon-remove">Kaldır</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Kupon kodu"
                    className="flex-1 h-11 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600"
                    data-testid="coupon-input" />
                  <button onClick={() => code.trim() && applyCoupon(code)} className="h-11 px-5 rounded-xl bg-[#121214] text-white text-sm font-semibold hover:bg-amber-600 transition-colors" data-testid="coupon-apply">
                    Uygula
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-neutral-500"><span>Ara toplam</span><span>{fmtTRY(cart?.subtotal)}</span></div>
              {cart?.campaign_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kampanya indirimi</span><span>-{fmtTRY(cart.campaign_discount)}</span></div>}
              {cart?.coupon_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kupon indirimi</span><span>-{fmtTRY(cart.coupon_discount)}</span></div>}
              <div className="flex justify-between text-neutral-500"><span>Kargo</span><span>{cart?.shipping_fee === 0 ? "Ücretsiz" : fmtTRY(cart?.shipping_fee)}</span></div>
              <div className="flex justify-between text-neutral-400 text-xs"><span>KDV dahil</span><span>{fmtTRY(cart?.vat_total)}</span></div>
              <div className="flex justify-between font-mono-ed font-bold text-xl pt-3 border-t border-[#E6E4DD]"><span>Genel Toplam</span><span data-testid="cart-page-total">{fmtTRY(cart?.total)}</span></div>
            </div>
            <button onClick={() => navigate("/odeme")} className="w-full h-13 py-3.5 mt-5 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors" data-testid="cart-page-checkout">
              Ödemeye Geç
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
